/**
 * CRM-A25: Finans bölümünün influencer hakedişi, kupon ve GeziPuan defteri ekranlarının metni.
 *
 * `metin/ortak.ts`in kuralı burada da geçerli: metin veri, koda serpiştirilmiş dize değil. Kod
 * değerleri (`ACCRUED`, `RECONCILIATION_SENT`, `ACTIVE`) çevrilmiyor, yalnız ekrana yazılırken
 * karşılığı gösteriliyor; karşılığı olmayan bir kod geldiğinde kodun kendisi yazılıyor.
 */

/** Hakedişin yedi durumu, vakanın 17-26. adımlarındaki sırayla. */
export const HAKEDIS_DURUMU: Readonly<Record<string, string>> = {
  ACCRUED: 'Biriken',
  RECONCILIATION_SENT: 'Mutabakata gönderildi',
  DISPUTED: 'İtiraz edildi',
  APPROVED: 'Onaylandı',
  INVOICE_UPLOADED: 'Fatura yüklendi',
  PAYMENT_ORDERED: 'Ödeme emri girildi',
  PAID: 'Ödendi',
};

export const ITIRAZ_SONUCU: Readonly<Record<string, string>> = {
  NO_CHANGE: 'Değişiklik yok',
  CORRECTED: 'Düzeltildi',
};

export const KUPON_DURUMU: Readonly<Record<string, string>> = {
  ACTIVE: 'Etkin',
  SUSPENDED: 'Askıya alındı',
};

/**
 * Kupon tanımının web sitesine ulaşıp ulaşmadığı.
 *
 * Ürün Sahibi'nin kuralı: `v3` bir **iç sürüm numarası**, kullanıcı onu hiç görmemeli
 * (`onyuz-kalite.spec.ts` 8. madde, menüde bir kez düzeltilmişti). Ölçüldü 28.08.2026: aynı
 * kelime bu tabloda duruyordu ve Finans ekranındaki her kupon satırında "v3’e ulaşmadı" yazıyordu.
 * Kullanıcının tanıdığı ad, menüde de yazan ad: **web sitesi**.
 */
export const YAYIN_DURUMU: Readonly<Record<string, string>> = {
  PENDING: 'Web sitesine ulaşmadı',
  PUBLISHED: 'Web sitesinde yayında',
};

export const REZERVASYON_DURUMU: Readonly<Record<string, string>> = {
  HELD: 'Tutuluyor',
  CONFIRMED: 'Kullanıldı',
  RELEASED: 'Bırakıldı',
};

/** Satış akışı vakası madde 20: tedarikçiye ödeme hazırlığının üç görünen hâli. */
export const TEDARIKCI_ODEME_DURUMU: Readonly<Record<string, string>> = {
  UNPAID: 'Ödeme emri girilmedi',
  PAYMENT_ORDERED: 'Ödeme emri girildi',
  PAID: 'Ödendi',
};

/** Satış akışı vakası madde 19: `OrderInvoice.state`. */
export const KESILEN_FATURA_DURUMU: Readonly<Record<string, string>> = {
  PENDING: 'Kesme denendi, sonuç bilinmiyor',
  ISSUED: 'Kesildi',
  FAILED: 'Kesilemedi',
};

/** Kesilecek fatura kuyruğunun durumları (`sales-invoice.readiness.ts` `WebInvoiceQueueState`). */
export const KESILECEK_FATURA_DURUMU: Readonly<Record<string, string>> = {
  ALIS_BEKLIYOR: 'Alış faturası bekliyor',
  ENGELLI: 'Eksik var',
  HAZIR: 'Kesilebilir',
  KESILDI: 'Kesildi',
};

/** Fatura kapılarının adları; uç `missing` listesinde anahtar döndürüyor. */
export const FATURA_KAPISI: Readonly<Record<string, string>> = {
  odeme_tamam: 'Ödeme',
  kur: 'Kur',
  alis_faturasi: 'Alış faturası',
  vize_harci: 'Vize harcı',
  musteri: 'Fatura adı',
  lisansnumarasi: 'Kimlik ya da vergi no',
  eposta: 'E-posta',
  telefonno: 'Telefon',
  adres: 'Fatura adresi',
  uyruk: 'Ülke',
  kdv_hesabi: 'KDV hesabı',
};

/** Fatura kimliğinin kaynağı (`resolveBillingParty`). */
export const KIMLIK_KAYNAGI: Readonly<Record<string, string>> = {
  KURUMSAL_FATURA: 'Kurumsal fatura bilgisi',
  SIPARIS_FATURA_KIMLIGI: 'Siparişteki fatura kimliği',
  MUSTERI_KAYDI: 'CRM müşteri kaydı',
  BAGLI_MUSTERI_YOK: 'Kaynak yok',
};

export const ALIS_TURU: Readonly<Record<string, string>> = {
  SUPPLIER_SERVICE: 'Tedarikçi hizmeti (tur, otel, transfer)',
  GOVERNMENT_FEE: 'Devlet harcı (vize)',
};

export const FATURA_SATIR_TURU: Readonly<Record<string, string>> = {
  SERVICE: 'Hizmet, yüzde 5 dahil',
  DISBURSEMENT: 'Devlet harcı, KDV dışı',
  DISCOUNT: 'İndirim, KDV sonrası',
};

export const KUR_KAYNAGI: Readonly<Record<string, string>> = {
  NOMOD_SETTLEMENT: 'Nomod tahsilatı',
  MAMOPAY_SETTLEMENT: 'Mamopay tahsilatı',
  BANK_STATEMENT: 'Banka ekstresi',
  MANUAL_LOCAL: 'Elle',
};

export const FINANS_METIN = {
  hakedisler: {
    ustBaslik: 'Influencer hakedişleri',
    baslik: 'Hakediş defteri',
    tarif: 'Kampanya hakedişleri, mutabakat ve ödeme durumu.',
    hata: 'Hakediş listesi okunamadı.',
    bos: 'Bu filtrede hakediş kaydı yok.',
    sutunDurum: 'Durum',
    sutunInfluencer: 'Influencer',
    sutunKampanya: 'Kampanya',
    sutunTutar: 'Tutar',
    sutunDonem: 'Dönem',
    sutunHesaplanma: 'Hesaplanma',
    tumDurumlar: 'Tüm durumlar',
    detay: 'Ayrıntı',
    kapat: 'Kapat',
    mutabakataGonder: 'Mutabakata gönder',
    mutabakataGonderildi: 'Mutabakat gönderildi.',
    itirazEt: 'İtirazı kaydet',
    itirazSebebi: 'İtiraz gerekçesi',
    itirazKaydedildi: 'İtiraz kaydedildi.',
    onayla: 'Doğrudan onayla',
    onaylandi: 'Hakediş onaylandı.',
    itiraziCoz: 'İtirazı kapat',
    itirazSonucu: 'Sonuç',
    duzeltilmisTutar: 'Düzeltilmiş tutar',
    not: 'Not (isteğe bağlı)',
    itirazKapatildi: 'İtiraz kapatıldı.',
    faturaYukle: 'Gider faturasını yükle',
    faturaAdi: 'Dosya adı',
    faturaTuru: 'İçerik türü',
    faturaIcerik: 'Fatura dosyası',
    faturaYuklendi: 'Fatura yüklendi.',
    faturalarBasligi: 'Yüklenmiş faturalar',
    faturaIndir: 'İndir',
    faturaYok: 'Henüz fatura yüklenmedi.',
    odemeEmriGir: 'Ödeme emrini gir',
    odemeReferansi: 'Emir referansı',
    odemeEmriGirildi: 'Ödeme emri girildi. Bu bir hazırlık kaydı — gerçek transfer CRM dışında yapılır.',
    odendiIsaretle: 'Dışarıda yapılan ödemeyi kaydet',
    odendiIsaretlendi: 'Ödeme kaydedildi.',
    surumUyusmuyor: 'Bu kayıt başka bir yerde değişti. Listeyi yenileyip tekrar deneyin.',
  },
  kuponlar: {
    ustBaslik: 'Influencer kuponları',
    baslik: 'Kupon defteri',
    tarif: 'Kupon kullanımları, kalan haklar ve web sitesi yayın durumu.',
    hata: 'Kupon listesi okunamadı.',
    bos: 'Bu filtrede kupon kaydı yok.',
    sutunKod: 'Kod',
    sutunDurum: 'Durum',
    sutunIndirim: 'İndirim',
    sutunBudget: 'Bütçe',
    sutunYayin: 'Yayın',
    tumDurumlar: 'Tüm durumlar',
    kalan: (kalan: string) => `Kalan: ${kalan}`,
    sinirsiz: 'Sınırsız',
    onaylanan: (adet: string) => `${adet} onaylandı`,
    tutulan: (adet: string) => `${adet} tutuluyor`,
    yenidenYayinla: 'Web sitesine yeniden gönder',
    yenidenYayinlandi: 'Yeniden yayınlama denendi.',
    yayinHatasi: 'Son yayın hatası',
    detay: 'Ayrıntı',
    kapat: 'Kapat',
    sonRezervasyonlar: 'Son rezervasyonlar',
    rezervasyonYok: 'Hiç rezervasyon yok.',
    bildirimlerBasligi: 'Influencer platformuna borçlu bildirimler',
    bildirimlerTarif:
      'Her onay ve iade influencer platformuna ulaşmalı. Burada bekleyen, pes edilen ve teslim ' +
      'edilmiş sayısı görünür.',
    /**
     * Gönderim durumunun **ekrana giden** cümlesi.
     *
     * Ölçüldü 28.08.2026: `/finans` ekranında uçtan gelen İngilizce cümle olduğu gibi
     * yazılıyordu — *"Confirmations and releases are posted to the influencer platform's signed
     * ingestion endpoint..."*. `dashboard.service.ts` aynı sorunu pazarlama tarafında
     * `DELIVERY_DETAIL_TR` ile çözmüştü; bu ekran onu almamıştı. Makine değeri (`delivery.state`)
     * değişmiyor, yalnız operatörün okuduğu cümle Türkçe.
     *
     * Uç tanımadık bir durum döndürürse (`?? sayfa.delivery.detail`) ham cümle yine görünür:
     * eksik bir karşılık, uydurulmuş bir karşılıktan iyidir.
     */
    gonderimDurumu: {
      CONFIGURED:
        'Onaylar ve iadeler influencer platformunun imzalı alım ucuna gönderiliyor; aynı bildirim ' +
        'iki kez gitse de kimliğinden ayırt ediliyor. Başarısız bir gönderim satırı burada tutar ve ' +
        'artan aralıklarla yeniden dener.',
      NOT_CONFIGURED:
        'Gönderim adresi tanımlı değil; hiçbir şey gönderilmiyor. Bu satırlar duran borcun kendisi.',
    } as Readonly<Record<string, string>>,
    bekleyen: 'Bekleyen',
    basarisiz: 'Başarısız',
    teslimEdilen: 'Teslim edilen',
    kuyruguCalistir: 'Kuyruğu şimdi çalıştır',
    kuyrukCalistirildi: (teslim: string, pesEdilen: string, kalan: string) =>
      `${teslim} teslim edildi, ${pesEdilen} satırda pes edildi, ${kalan} bekliyor.`,
    enEskiBekleyen: 'En eski bekleyen kayıt',
    hataBildirim: 'Bildirim kuyruğu okunamadı.',

    /**
     * Pes edilmiş satırlar: kuyruğun görünmeyen yarısı.
     *
     * Ölçüldü 28.08.2026, v3'teki 391 kez DOWN yazan kenarın CRM'deki karşılığı: onuncu denemede
     * duran bir bildirim listeden düşüyor, bir daha denenmiyor ve geriye yalnız "Başarısız: 3"
     * yazan bir sayaç kalıyordu. Sayacın altında ne olduğu hiçbir ekranda yoktu.
     */
    pesEdilenBasligi: 'Pes edilen bildirimler',
    pesEdilenTarif:
      'On denemede durdurulmuş satırlar. Kendiliğinden bir daha denenmezler: on kez düşen bir ' +
      'satır ağ dalgalanmasından değil, gövde, sır ya da adres yanlış olduğu için düşer. Sebebi ' +
      'düzelttikten sonra kuyruğa geri koyun.',
    pesEdilenYok: 'Pes edilen bildirim yok.',
    sutunRezervasyon: 'Rezervasyon',
    sutunDeneme: 'Deneme',
    sutunSonDeneme: 'Son deneme',
    sutunSonHata: 'Son hata',
    kuyrugaGeriKoy: 'Kuyruğa geri koy',
    kuyrugaGeriKonuldu: (adet: string) => `${adet} satır kuyruğa geri konuldu.`,
    hataGeriKoyma: 'Satırlar kuyruğa geri konulamadı.',
  },
  gezipuan: {
    ustBaslik: 'GeziPuan',
    baslik: 'GeziPuan borcu',
    tarif: 'Üyelere karşı toplam puan yükümlülüğü. Bekleyen krediler de taahhüde dahildir.',
    hata: 'GeziPuan borcu okunamadı.',
    harcanabilirBorc: 'Harcanabilir (açık) borç',
    beklemedeBorc: 'Bekleyen (henüz olgunlaşmamış)',
    toplamBorc: 'Toplam taahhüt',
    otuzGunIcinde: '30 gün içinde süresi dolacak',
    tutulan: 'Harcama sırasında tutulan',
    bakiyesiOlanUye: 'Bakiyesi olan üye sayısı',
    supurulmemis: 'Süresi geçmiş, henüz süpürülmemiş kredi',
    hesaplananAn: 'Hesaplandığı an',
    aramaBasligi: 'Bir üyenin defterini ara',
    musteriKimligi: 'Müşteri kimliği',
    getir: 'Getir',
    aramaHata: 'Üyenin defteri okunamadı.',
  },
  tedarikciOdemesi: {
    ustBaslik: 'Tedarikçi ödemeleri',
    baslik: 'Ödeme bekleyen maliyetler',
    tarif: 'Onaylanmış maliyetler. Ödeme emri hazırlık kaydıdır; banka transferi yapmaz.',
    hata: 'Ödeme bekleyen maliyet listesi okunamadı.',
    bos: 'Bu filtrede tedarikçi ödemesi yok.',
    sutunTedarikci: 'Tedarikçi',
    sutunAciklama: 'Açıklama',
    sutunSiparis: 'Sipariş',
    sutunTutar: 'Tutar',
    sutunDurum: 'Ödeme durumu',
    tumDurumlar: 'Tüm durumlar',
    detay: 'Ayrıntı',
    kapat: 'Kapat',
    odemeEmriGir: 'Ödeme emrini gir',
    odemeReferansi: 'Emir referansı',
    not: 'Not (isteğe bağlı)',
    odemeEmriGirildi: 'Ödeme emri girildi. Bu bir hazırlık kaydı, gerçek transfer CRM dışında yapılır.',
    odendiIsaretle: 'Dışarıda yapılan transferi kaydet',
    odendiIsaretlendi: 'Ödeme kaydedildi.',
    faturaYukle: 'Tedarikçinin gider faturasını yükle',
    faturaAdi: 'Dosya adı',
    faturaTuru: 'İçerik türü',
    faturaIcerik: 'Fatura dosyası',
    faturaYuklendi: 'Fatura yüklendi.',
    faturalarBasligi: 'Yüklenmiş gider faturaları',
    faturaIndir: 'İndir',
    faturaYok: 'Henüz gider faturası yüklenmedi.',
    odemeYok: 'Bu kalem için henüz ödeme emri girilmedi.',
  },
  kesilecekFatura: {
    ustBaslik: 'Satış faturası',
    baslik: 'Kesilecek faturalar',
    tarif: 'Ödenmiş web siparişleri. Önce her kalemin alış faturasını işleyin; KDV alış belli olunca belli olur. Sonra satış faturasını kesin.',
    hata: 'Kesilecek fatura listesi okunamadı.',
    bos: 'Kesilecek fatura yok.',
    sutunSiparis: 'Sipariş',
    sutunMusteri: 'Müşteri',
    sutunOdeme: 'Ödeme',
    sutunAlis: 'Alış',
    sutunTutar: 'Fatura tutarı',
    sutunKdv: 'KDV',
    sutunDurum: 'Durum',
    ac: 'Aç',
    kesilenleriGoster: 'Kesilenleri de göster',
    alisSayaci: (var_: number, toplam: number) => `${String(var_)} / ${String(toplam)}`,
    kapilar: 'Fatura koşulları',
    eksikYok: 'Eksik yok',
    onizleme: 'Fatura önizlemesi (AED)',
    onizlemeYok: 'Kur ya da alış eksik olduğu için önizleme hesaplanamadı.',
    satirTuru: 'Satır',
    aciklama: 'Açıklama',
    tutar: 'Tutar',
    kdv: 'KDV',
    brut: 'Brüt',
    indirim: 'İndirim',
    harc: 'Devlet harcı',
    odenecek: 'Fatura toplamı',
    kdvToplam: 'KDV toplamı',
    kalemler: 'Kalemler ve alış faturaları',
    alisYok: 'Bu kalemde alış faturası yok.',
    iptalEdildi: 'İptal edildi',
    iptal: 'İptal et',
    iptalNedeni: 'İptal nedeni',
    alisEkle: 'Alış faturası ekle',
    kalem: 'Kalem',
    alisTuru: 'Maliyet türü',
    tedarikci: 'Tedarikçi',
    alisNo: 'Alış fatura no',
    alisTarihi: 'Alış fatura tarihi',
    paraBirimi: 'Para birimi',
    alisTutari: 'Alış tutarı',
    alisKuru: 'AED kuru (1 birim kaç AED)',
    girdiKdv: 'Girdi KDV (AED)',
    not: 'Not',
    kaydet: 'Kaydet',
    kurBaslik: 'Satış kuru',
    kurDonduruldu: (kur: string, kaynak: string) => `Donduruldu: 1 birim = ${kur} AED (${kaynak})`,
    kurGerekmez: 'Sipariş AED, kur gerekmez.',
    kurDegeri: 'Kur (1 birim kaç AED)',
    kurKaynagi: 'Kaynak',
    kurTarihi: 'Paranın geldiği gün',
    kurDondur: 'Kuru dondur',
    mukerrer: 'Bu sipariş için fatura sistemde zaten vardı; var olan belge bağlandı.',
    sonHata: 'Son deneme hatası',
    proforma: 'Proforma',
    faturaKimligi: 'Fatura kimliği',
    kimlikKaynagi: (kaynak: string) => `Kaynak: ${kaynak}`,
    kimlikGizli: 'Değerleri görmek için müşteri okuma yetkisi gerekir.',
    kimlikYok: 'Sipariş için fatura kimliği gelmedi.',
    gecersizTutar: 'Tutar geçersiz.',
    gecersizKur: 'Kur geçersiz.',
    eksikAlan: 'Zorunlu alanları doldurun.',
    // Fatura kaydı (08.10.2026): satış faturası PayPartner'da kesilir, burada yalnız kaydı tutulur.
    tarif2: 'Satış faturasını PayPartner\'da kesin, sonra kaydını buradan siparişe bağlayın. CRM fatura kesmez.',
    alisIslendi: 'Alış faturası işlendi',
    alisIslenmedi: 'Alış faturası işlenmedi',
    alisUyarisi: 'Uyarı: bu siparişte alış faturası işlenmemiş kalem var. KDV oranı alışa bağlıdır; faturayı kesmeden önce alışı işlemeniz önerilir. Kayıt yine de girilebilir.',
    kdvOrani: 'KDV oranı (yüzde)',
    belge: 'Belge (PDF)',
    belgeAc: 'Belgeyi indir',
    belgeYok: 'Belge yok',
    duzelt: 'Düzelt',
    vazgec: 'Vazgeç',
    duzeltildi: 'Düzeltme kaydedildi.',
    belgeOkunamadi: 'Belge okunamadı.',
    satisKaydi: 'Satış faturası kaydı (PayPartner\'da kesilen)',
    satisNo: 'Fatura no',
    satisTarihi: 'Fatura tarihi',
    saticiSirket: 'Satıcı şirket',
    saticiAE: 'AE (MBD Tourism LLC)',
    saticiTR: 'TR (MBD Travel Turizm)',
    satisToplam: 'Fatura toplamı (KDV dahil)',
    satisKdv: 'KDV tutarı',
    satisKuru: 'AED kuru (1 birim kaç AED)',
    baglanti: 'PayPartner bağlantısı (https)',
    satisKaydet: 'Satış faturası kaydını gir',
    kesildi: (numara: string) => `Kesildi: ${numara}`,
    satisKayitliBaslik: 'Kayıtlı satış faturası',
    satisKaydedildi: (numara: string) => `Kaydedildi: ${numara}`,
    satisKaynakElle: 'Elle kaydedildi',
    payPartnerBaglanti: 'PayPartner',
    satisKaynakOtomasyon: 'Otomasyonla kesildi',
    uyariKodlari: { ALIS_FATURASI_ISLENMEDI: 'Kaydedildi. Uyarı: alış faturası işlenmemiş kalem var.' } as Readonly<Record<string, string>>,
  },

  alisFaturalari: {
    ustBaslik: 'Alış faturası',
    baslik: 'Alış faturaları',
    tarif: 'Tedarikçiden alınan faturalar. Yeni alış faturası, ilgili siparişin kaleminden girilir (Kesilecek faturalar, Aç).',
    hata: 'Alış faturası listesi okunamadı.',
    bos: 'Alış faturası kaydı yok.',
    ara: 'Tedarikçi ya da fatura no',
    iptalleriGoster: 'İptalleri de göster',
    sutunTedarikci: 'Tedarikçi',
    sutunNo: 'Fatura no',
    sutunTarih: 'Tarih',
    sutunTutar: 'Tutar',
    sutunKdv: 'KDV',
    sutunSiparis: 'Sipariş',
    sutunBelge: 'Belge',
    siparisiAc: 'Siparişi aç',
    iptal: 'İptal',
  },

  siparisFarki: {
    ustBaslik: 'Kâr',
    baslik: 'Sipariş başına alış, satış ve fark',
    tarif: 'Tutarlar KDV hariç, AED ve USD karşılığı (AED sabit bağlı kurla 3,6725). Satış faturası kaydı yoksa fark boş kalır.',
    hata: 'Rapor okunamadı.',
    bos: 'Ödenmiş sipariş yok.',
    sutunSiparis: 'Sipariş',
    sutunAlis: 'Alış (AED)',
    sutunSatis: 'Satış (AED)',
    sutunFark: 'Fark (AED)',
    sutunFarkUsd: 'Fark (USD)',
    sutunOran: 'Oran',
    satisYok: 'Satış faturası kaydı yok',
    alisYok: 'Alış faturası yok',
  },

  kesilenFaturalar: {
    ustBaslik: 'Müşteri faturaları',
    baslik: 'Kesilen faturalar',
    tarif: 'Kesilen faturalar ve kesme denemeleri. Yeni fatura sipariş finansından oluşturulur.',
    hata: 'Fatura listesi okunamadı.',
    bos: 'Bu filtrede fatura kaydı yok.',
    sutunSiparis: 'Sipariş',
    sutunMusteri: 'Müşteri',
    sutunNumara: 'Fatura no',
    sutunTutar: 'Tutar',
    sutunDurum: 'Durum',
    sutunTarih: 'İstek tarihi',
    tumDurumlar: 'Tüm durumlar',
    sonHata: 'Son hata',
  },

  /**
   * FR-20: marj kuralları.
   *
   * Bölüm **Finans sayfasında**, çünkü bu kuralı tutan iki rol (Finans ve Müdür) bu sayfayı
   * görüyor ve ikisi de kâr marjını okuma yetkisini taşıyor. Satış sayfasına konsaydı, kuralı
   * yazamayacak tek rolün (satışçı) ekranında duran bir düğme olurdu; yapılandırma sayfası ise
   * yalnız sistem yöneticisine açık ve o rol marj kuralını tutmuyor.
   *
   * Ölçülen kusur: kural motoru fiyat önerisinin dayanağı ve veritabanındaki tek kuralı tohum
   * yazmıştı. Uçlar 22.08'den beri açıktı; panelden ne yeni kural yazılabiliyor ne de var olan
   * bir kural kapatılabiliyordu.
   */
  marjKurallari: {
    ustBaslik: 'Fiyatlandırma politikası',
    baslik: 'Marj kuralları',
    tarif: 'Fiyat önerisi kuralları. En küçük öncelik sayısı uygulanır; boş kapsam alanları tümünü kapsar.',
    hata: 'Marj kuralları okunamadı.',
    bos: 'Tanımlı marj kuralı yok. Kural yokken fiyat önerisi verilemez.',
    sutunAd: 'Kural adı',
    sutunOncelik: 'Öncelik',
    sutunKapsam: 'Kapsam',
    sutunMarj: 'Marj',
    sutunDurum: 'Durum',
    etkin: 'Etkin',
    pasif: 'Kapalı',
    kapsamHepsi: 'her kalem, her destinasyon',
    kapsamTur: (tur: string) => `tür ${tur}`,
    kapsamDestinasyon: (yer: string) => `destinasyon ${yer}`,
    kapsamSezon: (baslangic: string, bitis: string) => `${baslangic}. aydan ${bitis}. aya`,
    kapsamEtiket: (etiket: string) => `müşteri etiketi ${etiket}`,
    kapsamBant: (alt: string, ust: string) => `${alt} – ${ust} arası`,
    kapsamAltSinir: (alt: string) => `${alt} ve üstü`,
    kapsamUstSinir: (ust: string) => `${ust} ve altı`,
    marjYuzde: (yuzde: string) => `%${yuzde}`,
    marjSabit: (tutar: string) => `sabit ${tutar}`,
    marjGizli: 'Marj yüzdesini görme yetkiniz yok.',
    kapat: 'Kuralı kapat',
    ac: 'Kuralı yeniden aç',
    kapatildi: (ad: string) => `${ad} kuralı kapatıldı; fiyat önerisinde artık kullanılmıyor.`,
    acildi: (ad: string) => `${ad} kuralı yeniden açıldı.`,

    yeniBaslik: 'Yeni kural',
    adEtiketi: 'Kural adı',
    adIpucu: 'En az üç harf',
    oncelikEtiketi: 'Öncelik',
    oncelikIpucu: 'Küçük sayı önce denenir. Boş bırakılırsa 100.',
    turEtiketi: 'Kalem türü',
    turHepsi: 'Her tür',
    destinasyonEtiketi: 'Destinasyon',
    destinasyonIpucu: 'Talep metninde geçen kelime. Boşsa her destinasyona uyar.',
    sezonBaslangicEtiketi: 'Sezon başlangıç ayı',
    sezonBitisEtiketi: 'Sezon bitiş ayı',
    sezonIpucu: '1 ile 12 arası. İkisi birlikte doldurulur ya da ikisi de boş kalır.',
    segmentEtiketi: 'Müşteri etiketi',
    altTutarEtiketi: 'Alt tutar sınırı',
    ustTutarEtiketi: 'Üst tutar sınırı',
    bantParaBirimiEtiketi: 'Bandın para birimi',
    bantParaBirimiIpucu: 'Üç harf, örnek: EUR. Tutar sınırı yazıldıysa gerekiyor.',
    marjYuzdeEtiketi: 'Marj yüzdesi',
    marjYuzdeIpucu: 'En çok iki ondalık basamak. Örnek: 18,50',
    marjSabitEtiketi: 'Sabit marj tutarı',
    olustur: 'Kuralı yaz',
    olusturuldu: (ad: string) => `${ad} kuralı yazıldı.`,
    adUyarisi: 'Kural adını yazın; en az üç harf.',
    marjUyarisi: 'Marj yüzdesi ya da sabit marj tutarından en az birini doldurun.',
    yuzdeBicimiUyarisi: 'Marj yüzdesini en çok iki ondalık basamakla yazın. Örnek: 18,50',
    sezonUyarisi: 'Sezon aralığının iki ucunu da doldurun ya da ikisini de boş bırakın.',
    bantUyarisi: 'Tutar sınırı yazdıysanız bandın para birimini de yazın.',
  },
} as const;
